/**
 * Calendar state persistence using sessionStorage.
 * Preserves selected date and view when navigating away and back.
 * Client version - no filters.
 */

export type ViewType = "daily" | "weekly" | "monthly";

const STORAGE_KEY = "bpreven-client-calendar-state";

export interface ICalendarStoredState {
  view: ViewType;
  date: string; // ISO date string (YYYY-MM-DD)
}

function parseStoredDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d;
}

export function getStoredCalendarState(): ICalendarStoredState | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ICalendarStoredState;
    if (!parsed.view || !parsed.date) return null;
    if (!parseStoredDate(parsed.date)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function setStoredCalendarState(state: Partial<ICalendarStoredState>): void {
  try {
    const current = getStoredCalendarState() || {
      view: "daily",
      date: new Date().toISOString().split("T")[0],
    };
    const merged: ICalendarStoredState = {
      view: state.view ?? current.view,
      date: state.date ?? current.date,
    };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // Ignore storage errors
  }
}

export function getStoredDateForView(view: ViewType): Date | null {
  const stored = getStoredCalendarState();
  if (!stored || stored.view !== view) return null;
  return parseStoredDate(stored.date);
}

export function getInitialDateForView(view: ViewType): Date {
  const stored = getStoredDateForView(view);
  if (stored) return stored;

  const now = new Date();
  if (view === "daily") return now;
  if (view === "weekly") {
    const day = now.getDay();
    const diff = now.getDate() - day;
    const weekStart = new Date(now);
    weekStart.setDate(diff);
    return weekStart;
  }
  return now;
}

export function saveDateForView(view: ViewType, date: Date): void {
  let dateStr: string;
  if (view === "daily") {
    dateStr = date.toISOString().split("T")[0];
  } else if (view === "weekly") {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    dateStr = d.toISOString().split("T")[0];
  } else {
    dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-01`;
  }
  setStoredCalendarState({ view, date: dateStr });
}
